"use client";

/**
 * OnboardingTour — global in-product Day-1 walkthrough.
 *
 * Wave 9 W9-6 / Task #99. Mounted once in `app/layout.tsx`. On mount:
 *   1. Query `trpc.user.getOnboardingState`
 *   2. If `tour_completed_at` OR `tour_dismissed_at` → render NOTHING
 *   3. Else → kick off a 6-step driver.js tour on the next paint frame
 *
 * Each Next click fires `trpc.user.updateOnboardingState` so progress persists
 * across devices (the founder runbook's Day-1 kickoff explicitly notes the
 * customer-admin Selin may be on a laptop in the meeting + a phone post-lunch).
 *
 * Library choice (driver.js v1.x, MIT):
 *   - 5.8KB gzipped vs shepherd.js ~30KB
 *   - No tippy.js / Popper.js dependency
 *   - First-class TypeScript types
 *   - "highlight + popover + step engine" matches our needs exactly; we don't
 *     need shepherd's modal/event-bus/CSS-theming surface area
 *   - Rationale captured in ONBOARDING-TOUR.verify.md §Library Choice
 *
 * SSR safety:
 *   driver.js touches `document` on import — we dynamic-import inside an
 *   effect so the bundle doesn't break Next's React Server Components.
 *   The component itself renders nothing to the DOM; all UI lives inside
 *   driver.js's portal.
 *
 * DOM contract:
 *   The tour expects these `data-tour-target` attributes (also listed in
 *   ONBOARDING-TOUR.verify.md so QA can grep for them):
 *     - data-component="app-header"                 → step 5 (Settings nav)
 *       (AppHeader.tsx already emits this)
 *     - data-tour-target="inbox-first-proposal"     → step 2 (/inbox)
 *     - data-tour-target="why-button"               → step 3 (Why drawer)
 *     - data-tour-target="nav-ask"                  → step 4 (/ask link)
 *     - data-tour-target="nav-audit"                → step 5 (/audit link)
 *     - data-tour-target="nav-settings"             → step 6 (/settings link)
 *   If a target is missing on the current route the step falls back to a
 *   centred modal popover (driver.js `element` undefined → modal mode).
 */

import * as React from "react";

import { trpc } from "@/lib/trpc-client";
import { detectLocale, makeT, type SupportedLocale } from "@/i18n";

// driver.js TypeScript shape — minimal subset we use. We re-declare instead
// of importing the package type to avoid the SSR-eager type pull.
interface DriverStep {
  element?: string | Element | null;
  popover: {
    title: string;
    description: string;
    side?: "top" | "right" | "bottom" | "left" | "over";
    align?: "start" | "center" | "end";
    nextBtnText?: string;
    prevBtnText?: string;
    showButtons?: ("next" | "previous" | "close")[];
    onNextClick?: (
      element: Element | undefined,
      step: DriverStep,
      api: { moveNext: () => void; destroy: () => void },
    ) => void;
    onPrevClick?: (
      element: Element | undefined,
      step: DriverStep,
      api: { movePrevious: () => void; destroy: () => void },
    ) => void;
    onCloseClick?: (
      element: Element | undefined,
      step: DriverStep,
      api: { destroy: () => void },
    ) => void;
  };
}

type DriverApi = {
  drive: () => void;
  destroy: () => void;
  moveNext: () => void;
  movePrevious: () => void;
  isActive: () => boolean;
};

/** Total step count must match user-types.ts ONBOARDING_TOTAL_STEPS. */
const TOTAL_STEPS = 6;

function nowIso(): string {
  return new Date().toISOString();
}

export function OnboardingTour(): React.JSX.Element | null {
  const [locale, setLocale] = React.useState<SupportedLocale>("tr-TR");
  const driverRef = React.useRef<DriverApi | null>(null);

  const stateQuery = trpc.user.getOnboardingState.useQuery(undefined, {
    // Async-mount discipline: do NOT block first-page-render. We retry
    // silently if the call fails — onboarding is best-effort UX.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const updateMutation = trpc.user.updateOnboardingState.useMutation({
    // Silent on error: a failed write should NOT interrupt the tour UI —
    // it just means a future browser session might re-show step N. Per
    // W9-6 brief ("Tour MUST be dismissable + restartable — never force-show").
    onError: () => undefined,
  });

  // Resolve locale on the client where window is available.
  React.useEffect(() => {
    setLocale(detectLocale());
  }, []);

  const shouldShow = React.useMemo(() => {
    const data = stateQuery.data;
    if (!data) return false;
    // Wave 31 P0-10: manuel re-trigger via localStorage flag.
    if (typeof window !== "undefined" && localStorage.getItem("commerco-force-tour") === "1") {
      return true;
    }
    if (data.tour_completed_at) return false;
    if (data.tour_dismissed_at) return false;
    return true;
  }, [stateQuery.data]);

  React.useEffect(() => {
    if (!shouldShow) return;
    if (typeof window === "undefined") return;

    let cancelled = false;
    const t = makeT(locale);

    async function bootDriver() {
      // Dynamic import keeps SSR + first-render quiet. The package is MIT,
      // ~6KB gzipped, no transitive deps that touch React or fetch.
      // Package is "driver.js" not "driver" — bug caught at first live boot.
      const mod = await import("driver.js").catch(() => null);
      // Side-effect CSS import — also dynamic so it lands only when the tour
      // is actually going to run.
      await import("driver.js/dist/driver.css").catch(() => undefined);
      if (cancelled || !mod) return;

      const userId = stateQuery.data; // for closures + memo deps

      function patch(
        patchBody: Parameters<typeof updateMutation.mutate>[0],
      ): void {
        try {
          updateMutation.mutate(patchBody);
        } catch {
          // Best-effort write per W9-6 brief.
        }
      }

      const steps: DriverStep[] = [
        // Step 1 — Welcome modal (no element → centred)
        {
          popover: {
            title: t("onboarding.welcome.title"),
            description: t("onboarding.welcome.body"),
            nextBtnText: t("onboarding.welcome.cta_start"),
            showButtons: ["next", "close"],
            onNextClick: (_el, _step, api) => {
              patch({ tour_started_at: nowIso(), last_step_completed: 1 });
              api.moveNext();
            },
            onCloseClick: (_el, _step, api) => {
              patch({ tour_dismissed_at: nowIso() });
              api.destroy();
            },
          },
        },
        // Step 2 — /inbox first proposal card
        {
          element: '[data-tour-target="inbox-first-proposal"]',
          popover: {
            title: t("onboarding.step_inbox.title"),
            description: t("onboarding.step_inbox.body"),
            side: "bottom",
            align: "start",
            nextBtnText: t("onboarding.controls.next"),
            prevBtnText: t("onboarding.controls.previous"),
            showButtons: ["next", "previous", "close"],
            onNextClick: (_el, _step, api) => {
              patch({ last_step_completed: 2 });
              api.moveNext();
            },
            onCloseClick: (_el, _step, api) => {
              patch({ tour_dismissed_at: nowIso() });
              api.destroy();
            },
          },
        },
        // Step 3 — Why drawer / Why button on first proposal
        {
          element: '[data-tour-target="why-button"]',
          popover: {
            title: t("onboarding.step_why.title"),
            description: t("onboarding.step_why.body"),
            side: "left",
            nextBtnText: t("onboarding.controls.next"),
            prevBtnText: t("onboarding.controls.previous"),
            showButtons: ["next", "previous", "close"],
            onNextClick: (el, _step, api) => {
              // Auto-click the Why button so the drawer opens behind the
              // next popover — gives the customer the actual content.
              if (el instanceof HTMLElement) {
                try {
                  el.click();
                } catch {
                  // ignore — popover continues
                }
              }
              patch({ last_step_completed: 3 });
              api.moveNext();
            },
            onCloseClick: (_el, _step, api) => {
              patch({ tour_dismissed_at: nowIso() });
              api.destroy();
            },
          },
        },
        // Step 4 — /ask nav link
        {
          element: '[data-tour-target="nav-ask"]',
          popover: {
            title: t("onboarding.step_ask.title"),
            description: t("onboarding.step_ask.body"),
            side: "bottom",
            align: "center",
            nextBtnText: t("onboarding.controls.next"),
            prevBtnText: t("onboarding.controls.previous"),
            showButtons: ["next", "previous", "close"],
            onNextClick: (_el, _step, api) => {
              patch({ last_step_completed: 4 });
              api.moveNext();
            },
            onCloseClick: (_el, _step, api) => {
              patch({ tour_dismissed_at: nowIso() });
              api.destroy();
            },
          },
        },
        // Step 5 — /audit nav link
        {
          element: '[data-tour-target="nav-audit"]',
          popover: {
            title: t("onboarding.step_audit.title"),
            description: t("onboarding.step_audit.body"),
            side: "bottom",
            align: "center",
            nextBtnText: t("onboarding.controls.next"),
            prevBtnText: t("onboarding.controls.previous"),
            showButtons: ["next", "previous", "close"],
            onNextClick: (_el, _step, api) => {
              patch({ last_step_completed: 5 });
              api.moveNext();
            },
            onCloseClick: (_el, _step, api) => {
              patch({ tour_dismissed_at: nowIso() });
              api.destroy();
            },
          },
        },
        // Step 6 — /settings link (final)
        {
          element: '[data-tour-target="nav-settings"]',
          popover: {
            title: t("onboarding.step_settings.title"),
            description: t("onboarding.step_settings.body"),
            side: "bottom",
            align: "end",
            nextBtnText: t("onboarding.completion.cta_inbox"),
            prevBtnText: t("onboarding.controls.previous"),
            showButtons: ["next", "previous", "close"],
            onNextClick: (_el, _step, api) => {
              patch({
                tour_completed_at: nowIso(),
                last_step_completed: TOTAL_STEPS,
              });
              // Light confetti — pure CSS, no canvas-confetti dep.
              showConfetti();
              api.destroy();
              // Soft route: nudge to /inbox so the customer lands where the
              // first proposals will pop on Day-2/3.
              try {
                window.location.pathname = "/inbox";
              } catch {
                // SSR / sandboxed iframe — ignore.
              }
            },
            onCloseClick: (_el, _step, api) => {
              patch({ tour_dismissed_at: nowIso() });
              api.destroy();
            },
          },
        },
      ];

      // driver.js v1 exports a `driver` factory function. Both default and
      // named export forms exist in the wild — pick whichever loaded.
      const driverFactory =
        (mod as { driver?: (cfg: unknown) => DriverApi }).driver ??
        (mod as unknown as { default: (cfg: unknown) => DriverApi }).default;

      if (typeof driverFactory !== "function") {
        // Library API drifted — fall back to silent no-op so the customer
        // still sees /inbox. /welcome fallback page covers the 6 steps
        // in static form.
        return;
      }

      const api = driverFactory({
        showProgress: true,
        steps,
        // Allow tour to be dismissed by clicking outside / pressing Escape;
        // driver.js fires onCloseClick on the active step.
        allowClose: true,
        // Smooth scrolling so off-screen targets ease into view.
        smoothScroll: true,
        // 6KB → safe to keep z-index above our modals (audit drawer = z-50).
        stagePadding: 8,
        stageRadius: 8,
      });

      driverRef.current = api;
      // requestAnimationFrame defers driver.drive() past first paint so we
      // don't compete with hydration. Honors "don't block first-page-render"
      // constraint from the W9-6 brief.
      requestAnimationFrame(() => {
        if (!cancelled) api.drive();
      });
    }

    void bootDriver();

    return () => {
      cancelled = true;
      try {
        driverRef.current?.destroy();
      } catch {
        // ignore
      }
      driverRef.current = null;
    };
  }, [shouldShow, locale, stateQuery.data, updateMutation]);

  // Nothing to render in our own DOM — driver.js owns the portal.
  return null;
}

/**
 * Minimal CSS-only confetti. 24 short-lived divs that float up + fade.
 *
 * Why hand-roll instead of canvas-confetti / party-js:
 *   - 1 dep less; bundle stays under 10KB for the whole onboarding feature
 *   - Triggers exactly once per lifetime per user → cost-of-perfect is low
 *   - Founder-week-1 ethos: ship the simplest thing that delights
 */
function showConfetti(): void {
  if (typeof document === "undefined") return;
  const host = document.createElement("div");
  host.setAttribute("data-component", "onboarding-confetti");
  host.setAttribute(
    "style",
    [
      "position: fixed",
      "inset: 0",
      "pointer-events: none",
      "overflow: hidden",
      "z-index: 60",
    ].join(";"),
  );
  const colors = [
    "#16b3a4",
    "#f97316",
    "#3b82f6",
    "#ec4899",
    "#22c55e",
    "#eab308",
  ];
  for (let i = 0; i < 24; i += 1) {
    const piece = document.createElement("span");
    const left = Math.random() * 100;
    const color = colors[i % colors.length];
    const dur = 1.6 + Math.random() * 1.4;
    const delay = Math.random() * 0.3;
    const rot = Math.random() * 360;
    piece.setAttribute(
      "style",
      [
        "position: absolute",
        `left: ${left}%`,
        "top: -16px",
        "width: 8px",
        "height: 14px",
        `background: ${color}`,
        "border-radius: 2px",
        `transform: rotate(${rot}deg)`,
        `animation: commerco-confetti ${dur}s ease-in ${delay}s forwards`,
      ].join(";"),
    );
    host.appendChild(piece);
  }
  // Inject the keyframes once.
  if (!document.getElementById("commerco-confetti-kf")) {
    const styleEl = document.createElement("style");
    styleEl.id = "commerco-confetti-kf";
    styleEl.textContent =
      "@keyframes commerco-confetti { 0% { transform: translateY(0) rotate(0); opacity: 1; } 100% { transform: translateY(120vh) rotate(720deg); opacity: 0; } }";
    document.head.appendChild(styleEl);
  }
  document.body.appendChild(host);
  // Auto-remove host after 4s so we never leak DOM.
  window.setTimeout(() => {
    try {
      host.remove();
    } catch {
      // ignore
    }
  }, 4_000);
}
